// Adapted from '@atlassian/jira-issue-table/src/common/fields/issue-key';
import React, { memo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useIntl } from '@atlassian/jira-intl';
import ListCellWithPopup from '@atlassian/jira-list-with-popup/src/ui/main.tsx';
import {
	TagItem,
	TagItemPopupContentWrapper,
} from '@atlassian/jira-list-with-popup/src/ui/tag-item/index.tsx';
import type { AffectedServicesCell$key } from '@atlassian/jira-relay/src/__generated__/AffectedServicesCell.graphql';
import { isNonNullish } from '../../../utils/index.tsx';
import ErrorCell from '../../error-cell/index.tsx';
import { useIsSingleLineRowHeightEnabled } from '../../../../controllers/features/selectors.tsx';
import messages from './messages.tsx';

export type Props = {
	fieldRef: AffectedServicesCell$key;
	isReadOnlyWithoutHoverPopover?: boolean;
};
export const AffectedServicesCell = memo<Props>(
	({ fieldRef, isReadOnlyWithoutHoverPopover }: Props) => {
		const { formatMessage } = useIntl();
		const data = useFragment<AffectedServicesCell$key>(
			graphql`
				fragment AffectedServicesCell on JiraAffectedServicesField {
					selectedAffectedServicesConnection {
						edges {
							node {
								serviceId
								name
							}
						}
					}
				}
			`,
			fieldRef,
		);

		const affectedServices = data.selectedAffectedServicesConnection?.edges;
		const filteredAffectedServices =
			affectedServices
				?.map((edge) =>
					edge?.node?.name != null
						? {
								key: edge.node.serviceId,
								name: edge.node.name,
							}
						: null,
				)
				.filter(isNonNullish) ?? [];

		const isSingleLineRowHeightEnabled =
			useIsSingleLineRowHeightEnabled() && !isReadOnlyWithoutHoverPopover;

		if (!affectedServices || affectedServices.length !== filteredAffectedServices.length) {
			return <ErrorCell cellType="affectedServices" reason="Missing required fields" />;
		}

		return (
			<ListCellWithPopup
				items={filteredAffectedServices}
				ItemComponent={TagItem}
				PopupContentWrapper={TagItemPopupContentWrapper}
				showMoreTooltip={formatMessage(messages.triggerButton)}
				isHoverPopoverEnabled={isSingleLineRowHeightEnabled}
			/>
		);
	},
);
